import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  LayoutDashboard, MessageSquare, Star, 
  LogOut, Trash2, Eye, EyeOff, Check, Menu, X, Settings
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [stats, setStats] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [messages, setMessages] = useState([]);
  const [siteSettings, setSiteSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const [settingsForm, setSettingsForm] = useState({});

  const [token, setToken] = useState(localStorage.getItem('admin_token'));

  useEffect(() => {
    const storedToken = localStorage.getItem('admin_token');
    if (!storedToken) {
      navigate('/admin');
      return;
    }
    setToken(storedToken);
    fetchAllData();
  }, [navigate]);

  const fetchAllData = async () => {
    const currentToken = localStorage.getItem('admin_token');
    if (!currentToken) {
      navigate('/admin');
      return;
    }
    const headers = { headers: { Authorization: `Bearer ${currentToken}` } };
    
    try {
      const [statsRes, reviewsRes, messagesRes, settingsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, headers),
        axios.get(`${API}/reviews?show_all=true`),
        axios.get(`${API}/contact`, headers),
        axios.get(`${API}/settings`),
      ]);
      setStats(statsRes.data);
      setReviews(reviewsRes.data);
      setMessages(messagesRes.data);
      setSiteSettings(settingsRes.data);
      setSettingsForm(settingsRes.data);
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        localStorage.removeItem('admin_token');
        navigate('/admin');
      } else {
        toast.error('Failed to fetch data');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin');
  };

  const getAuthHeader = () => {
    const currentToken = localStorage.getItem('admin_token');
    return { headers: { Authorization: `Bearer ${currentToken}` } };
  };

  // Review handlers
  const handleToggleReview = async (id, isVisible) => {
    try {
      await axios.put(`${API}/reviews/${id}/visibility?is_visible=${!isVisible}`, {}, getAuthHeader());
      toast.success('Review visibility updated');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to update review');
    }
  };

  const handleDeleteReview = async (id) => {
    if (!window.confirm('Delete this review?')) return;
    try {
      await axios.delete(`${API}/reviews/${id}`, getAuthHeader());
      toast.success('Review deleted');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to delete review');
    }
  };

  // Message handlers
  const handleMarkRead = async (id) => {
    try {
      await axios.put(`${API}/contact/${id}/read`, {}, getAuthHeader());
      fetchAllData();
    } catch (error) {
      toast.error('Failed to mark as read');
    }
  };

  const handleDeleteMessage = async (id) => {
    if (!window.confirm('Delete this message?')) return;
    try {
      await axios.delete(`${API}/contact/${id}`, getAuthHeader());
      toast.success('Message deleted');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to delete message');
    }
  };

  // Settings handlers
  const handleSaveSettings = async () => {
    try {
      await axios.put(`${API}/settings`, settingsForm, getAuthHeader());
      toast.success('Settings saved!');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to save settings');
    }
  };

  const menuItems = [
    { id: 'dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { id: 'settings', icon: Settings, label: 'Site Settings' },
    { id: 'reviews', icon: Star, label: 'Reviews' },
    { id: 'messages', icon: MessageSquare, label: 'Messages', badge: stats?.unread_messages },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-white font-body">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] flex">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-[#0A0A0A] border-b border-white/10 flex items-center justify-between px-4 z-50">
        <button onClick={() => setSidebarOpen(!sidebarOpen)} className="text-white p-2">
          {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
        <span className="font-heading text-white">Admin Panel</span>
        <button onClick={handleLogout} className="text-white/60 p-2">
          <LogOut className="w-5 h-5" />
        </button>
      </div>

      {/* Sidebar */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-40 w-64 bg-[#0A0A0A] border-r border-white/10 
        transform transition-transform duration-300
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        pt-16 lg:pt-0
      `}>
        <div className="p-6 border-b border-white/10 hidden lg:block">
          <h1 className="font-heading text-xl text-white">Perry D Tires</h1>
          <p className="font-body text-white/40 text-sm">Admin Panel</p>
        </div>

        <nav className="p-4 space-y-2">
          {menuItems.map((item) => (
            <button
              key={item.id}
              onClick={() => { setActiveTab(item.id); setSidebarOpen(false); }}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-none transition-all ${
                activeTab === item.id
                  ? 'bg-[#FF2E2E]/20 text-[#FF2E2E] border-l-2 border-[#FF2E2E]'
                  : 'text-white/60 hover:bg-white/5 hover:text-white'
              }`}
            >
              <item.icon className="w-5 h-5" />
              <span className="font-body">{item.label}</span>
              {item.badge > 0 && (
                <span className="ml-auto bg-[#FF2E2E] text-white text-xs px-2 py-0.5 rounded-full">
                  {item.badge}
                </span>
              )}
            </button>
          ))}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-white/10 hidden lg:block">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 text-white/60 hover:text-[#FF2E2E] transition-colors"
          >
            <LogOut className="w-5 h-5" />
            <span className="font-body">Logout</span>
          </button>
        </div>
      </aside>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-30 lg:hidden" 
          onClick={() => setSidebarOpen(false)} 
        />
      )}

      {/* Main Content */}
      <main className="flex-1 p-4 lg:p-8 pt-20 lg:pt-8 overflow-auto">
        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <h2 className="font-heading text-2xl lg:text-3xl text-white mb-6">Dashboard</h2>
            <div className="grid grid-cols-2 gap-4">
              <div className="glass p-4 lg:p-6 border-l-4 border-yellow-500">
                <p className="font-body text-white/60 text-sm">Reviews</p>
                <p className="font-heading text-2xl lg:text-3xl text-white">{stats?.reviews || 0}</p>
              </div>
              <div className="glass p-4 lg:p-6 border-l-4 border-green-500">
                <p className="font-body text-white/60 text-sm">Messages</p>
                <p className="font-heading text-2xl lg:text-3xl text-white">
                  {stats?.messages || 0}
                  {stats?.unread_messages > 0 && (
                    <span className="text-sm text-[#FF2E2E] ml-2">({stats.unread_messages} new)</span>
                  )}
                </p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Reviews Tab */}
        {activeTab === 'reviews' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <h2 className="font-heading text-2xl lg:text-3xl text-white mb-6">Reviews</h2>
            <div className="space-y-4">
              {reviews.map((review) => (
                <div key={review.id} className={`glass p-4 ${!review.is_visible ? 'opacity-50' : ''}`}>
                  <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="font-heading text-white">{review.name}</span>
                        <div className="flex">
                          {[...Array(5)].map((_, i) => (
                            <Star key={i} className={`w-4 h-4 ${i < review.rating ? 'fill-[#FF2E2E] text-[#FF2E2E]' : 'text-white/20'}`} />
                          ))}
                        </div>
                      </div>
                      <p className="font-body text-white/70">{review.comment}</p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleToggleReview(review.id, review.is_visible)}
                        className={`p-2 ${review.is_visible ? 'text-green-500' : 'text-white/40'} hover:text-[#FF2E2E]`}
                        title={review.is_visible ? 'Hide review' : 'Show review'}
                      >
                        {review.is_visible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                      </button>
                      <button
                        onClick={() => handleDeleteReview(review.id)}
                        className="p-2 text-white/60 hover:text-red-500"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Messages Tab */}
        {activeTab === 'messages' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <h2 className="font-heading text-2xl lg:text-3xl text-white mb-6">Contact Messages</h2>
            <div className="space-y-4">
              {messages.length === 0 ? (
                <div className="glass p-8 text-center">
                  <p className="text-white/60">No messages yet</p>
                </div>
              ) : (
                messages.map((msg) => (
                  <div key={msg.id} className={`glass p-4 border-l-4 ${msg.is_read ? 'border-white/20' : 'border-[#FF2E2E]'}`}>
                    <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                          <span className="font-heading text-white">{msg.name}</span>
                          {!msg.is_read && (
                            <span className="bg-[#FF2E2E] text-white text-xs px-2 py-0.5 rounded-full">New</span>
                          )}
                        </div>
                        <p className="font-body text-white/60 text-sm">{msg.email} {msg.phone && `• ${msg.phone}`}</p>
                        <p className="font-body text-white/80 mt-2">{msg.message}</p>
                        <p className="font-body text-white/40 text-xs mt-2">
                          {new Date(msg.created_at).toLocaleString()}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        {!msg.is_read && (
                          <button
                            onClick={() => handleMarkRead(msg.id)}
                            className="p-2 text-white/60 hover:text-green-500"
                            title="Mark as read"
                          >
                            <Check className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => handleDeleteMessage(msg.id)}
                          className="p-2 text-white/60 hover:text-red-500"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </motion.div>
        )}

        {/* Site Settings Tab */}
        {activeTab === 'settings' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="settings-tab">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
              <h2 className="font-heading text-2xl lg:text-3xl text-white">Site Settings</h2>
              <Button
                onClick={handleSaveSettings}
                className="bg-[#FF2E2E] hover:bg-[#D61E1E] rounded-none"
                data-testid="save-settings-btn"
              >
                <Check className="w-4 h-4 mr-2" /> Save Changes
              </Button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Hero Section Settings */}
              <div className="glass p-6" data-testid="hero-settings">
                <h3 className="font-heading text-lg text-[#FF2E2E] mb-4 flex items-center gap-2">
                  <span className="w-2 h-2 bg-[#FF2E2E]"></span>
                  Hero Section
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="font-body text-white/60 text-sm block mb-2">Main Title</label>
                    <Input
                      value={settingsForm.hero_title || ''}
                      onChange={(e) => setSettingsForm({ ...settingsForm, hero_title: e.target.value })}
                      placeholder="PERRY D TIRES:"
                      className="bg-[#121212] border-white/10 text-white rounded-none"
                      data-testid="hero-title-input"
                    />
                  </div>
                  <div>
                    <label className="font-body text-white/60 text-sm block mb-2">Subtitle</label>
                    <Input
                      value={settingsForm.hero_subtitle || ''}
                      onChange={(e) => setSettingsForm({ ...settingsForm, hero_subtitle: e.target.value })}
                      placeholder="Professional Tire Service 2026"
                      className="bg-[#121212] border-white/10 text-white rounded-none"
                      data-testid="hero-subtitle-input"
                    />
                  </div>
                  <div>
                    <label className="font-body text-white/60 text-sm block mb-2">Tagline</label>
                    <Input
                      value={settingsForm.hero_tagline || ''}
                      onChange={(e) => setSettingsForm({ ...settingsForm, hero_tagline: e.target.value })}
                      placeholder="New and Used Tires. Quality and Speed."
                      className="bg-[#121212] border-white/10 text-white rounded-none"
                      data-testid="hero-tagline-input"
                    />
                  </div>
                  <div>
                    <label className="font-body text-white/60 text-sm block mb-2">Promo Banner</label>
                    <Input
                      value={settingsForm.hero_promo || ''}
                      onChange={(e) => setSettingsForm({ ...settingsForm, hero_promo: e.target.value })}
                      placeholder="FREE TIRE CHANGE & BALANCING"
                      className="bg-[#121212] border-white/10 text-white rounded-none"
                      data-testid="hero-promo-input"
                    />
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              <div className="glass p-6" data-testid="contact-settings">
                <h3 className="font-heading text-lg text-[#FF2E2E] mb-4 flex items-center gap-2">
                  <span className="w-2 h-2 bg-[#FF2E2E]"></span>
                  Contact Information
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="font-body text-white/60 text-sm block mb-2">Phone Number</label>
                    <Input
                      value={settingsForm.phone || ''}
                      onChange={(e) => setSettingsForm({ ...settingsForm, phone: e.target.value })}
                      placeholder="(410) 123-4567"
                      className="bg-[#121212] border-white/10 text-white rounded-none"
                      data-testid="phone-input"
                    />
                  </div>
                  <div>
                    <label className="font-body text-white/60 text-sm block mb-2">Email</label>
                    <Input
                      value={settingsForm.email || ''}
                      onChange={(e) => setSettingsForm({ ...settingsForm, email: e.target.value })}
                      placeholder="info@perrydtires.com"
                      className="bg-[#121212] border-white/10 text-white rounded-none"
                      data-testid="email-input"
                    />
                  </div>
                  <div>
                    <label className="font-body text-white/60 text-sm block mb-2">Address Line 1</label>
                    <Input
                      value={settingsForm.address_line1 || ''}
                      onChange={(e) => setSettingsForm({ ...settingsForm, address_line1: e.target.value })}
                      placeholder="51 Main Street, Suite B"
                      className="bg-[#121212] border-white/10 text-white rounded-none"
                      data-testid="address1-input"
                    />
                  </div>
                  <div>
                    <label className="font-body text-white/60 text-sm block mb-2">Address Line 2</label>
                    <Input
                      value={settingsForm.address_line2 || ''}
                      onChange={(e) => setSettingsForm({ ...settingsForm, address_line2: e.target.value })}
                      placeholder="Reisterstown, MD 21136"
                      className="bg-[#121212] border-white/10 text-white rounded-none"
                      data-testid="address2-input"
                    />
                  </div>
                </div>
              </div>

              {/* Business Hours */}
              <div className="glass p-6" data-testid="hours-settings">
                <h3 className="font-heading text-lg text-[#FF2E2E] mb-4 flex items-center gap-2">
                  <span className="w-2 h-2 bg-[#FF2E2E]"></span>
                  Business Hours
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="font-body text-white/60 text-sm block mb-2">Weekday Hours</label>
                    <Input
                      value={settingsForm.hours_weekday || ''}
                      onChange={(e) => setSettingsForm({ ...settingsForm, hours_weekday: e.target.value })}
                      placeholder="Mon-Sat: 8AM - 6PM"
                      className="bg-[#121212] border-white/10 text-white rounded-none"
                      data-testid="hours-weekday-input"
                    />
                  </div>
                  <div>
                    <label className="font-body text-white/60 text-sm block mb-2">Weekend Hours</label>
                    <Input
                      value={settingsForm.hours_weekend || ''}
                      onChange={(e) => setSettingsForm({ ...settingsForm, hours_weekend: e.target.value })}
                      placeholder="Sunday: Closed"
                      className="bg-[#121212] border-white/10 text-white rounded-none"
                      data-testid="hours-weekend-input"
                    />
                  </div>
                </div>
              </div>

              {/* Pricing & Social Links */}
              <div className="glass p-6" data-testid="pricing-social-settings">
                <h3 className="font-heading text-lg text-[#FF2E2E] mb-4 flex items-center gap-2">
                  <span className="w-2 h-2 bg-[#FF2E2E]"></span>
                  Pricing & Social
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="font-body text-white/60 text-sm block mb-2">Used Tire Price ($)</label>
                    <Input
                      type="number"
                      value={settingsForm.used_tire_price || ''}
                      onChange={(e) => setSettingsForm({ ...settingsForm, used_tire_price: parseInt(e.target.value) || 50 })}
                      placeholder="50"
                      className="bg-[#121212] border-white/10 text-white rounded-none"
                      data-testid="used-tire-price-input"
                    />
                  </div>
                  <div>
                    <label className="font-body text-white/60 text-sm block mb-2">Facebook URL</label>
                    <Input
                      value={settingsForm.facebook_url || ''}
                      onChange={(e) => setSettingsForm({ ...settingsForm, facebook_url: e.target.value })}
                      placeholder="https://facebook.com/perrydtires"
                      className="bg-[#121212] border-white/10 text-white rounded-none"
                      data-testid="facebook-url-input"
                    />
                  </div>
                  <div>
                    <label className="font-body text-white/60 text-sm block mb-2">Instagram URL</label>
                    <Input
                      value={settingsForm.instagram_url || ''}
                      onChange={(e) => setSettingsForm({ ...settingsForm, instagram_url: e.target.value })}
                      placeholder="https://instagram.com/perrydtires"
                      className="bg-[#121212] border-white/10 text-white rounded-none"
                      data-testid="instagram-url-input"
                    />
                  </div>
                  <div>
                    <label className="font-body text-white/60 text-sm block mb-2">Twitter URL</label>
                    <Input
                      value={settingsForm.twitter_url || ''}
                      onChange={(e) => setSettingsForm({ ...settingsForm, twitter_url: e.target.value })}
                      placeholder="https://twitter.com/perrydtires"
                      className="bg-[#121212] border-white/10 text-white rounded-none"
                      data-testid="twitter-url-input"
                    />
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </main>
    </div>
  );
};

export default AdminDashboard;
